// react and external-libs
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
// MUI
import {
  Box,
  Button,
  Chip,
  Divider,
  Drawer,
  FormControlLabel,
  FormGroup,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  Skeleton,
  Stack,
  Switch,
  Theme,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material';
import { AccessTimeOutlined, Add, Cancel, CheckCircle, Close, Delete, DeleteOutline, EditOutlined, MoreVert } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import StartNewQuestionnaireModal from '../../StartNewQuestionnaireModal';
// redux
import { useAppDispatch, useAppSelector } from '../../../../app/hooks';
import { QuestionnaireType, fetchPublishedQuestionnaire } from '../../../../app/store/surveySlice';
// interface
import { BaselineStatusMap, Survey } from '../../../../interfaces/survey.interface';
import { Workflow, WorkflowTypeKey } from '../../../../interfaces/workflow.interface';
import { AssessmentSurvey } from '../../../../interfaces/assessment.interface';
import User, { Badge, Company, CompanyApplication, Contact } from '../../../../interfaces/users.interface';
// service
import { WorkflowStatus as WorkflowStatusInterface, createNewWorkflow, dedupeWorkflowsById, fetchWorkflows, sortQuestionnairesNewestFirst, workflowLabels, workflowVersion, handleDeativateWorkflows } from '../../../../services/workflow';
import { useWorkflowMap } from '../../../../hooks/useWorkflowMap';
import { useFeatureFlagChecker } from '../../../../hooks/useFeatureFlag';
import { selectBadgeFromArray, fetchGoldBadgeLabel, fetchBlueBadgeLabel, recentBadgeByType, BLUE_BADGE_LABEL, GOLD_BADGE_LABEL, SILVER_BADGE_LABEL, GOLD_NEXT_GEN_BADGE_LABEL, GOLD_STAR_BADGE_LABEL } from '../../../../services/badge';
import createSurvey, { fetchSurvey } from '../../../../services/surveys';
import { fetchAssessmentSurvey, updateAssessmentSurvey, getSiteCloudStatus } from '../../../../services/assessments';
import { fetchAll } from '../../../../app/store/questionnaireSlice';
// components
import GetBadge from '../../GetBadge';
import WorkflowStatus from '../../WorkflowStatus';
import WorkflowButtons from '../../WorkflowButtons';
import AssessmentForm from '../../../Assessments/NewAssessmentModal';
import StandardDialog, { StandardDialogActions } from '../../../Modals/StandardDialog';
import { updateWorkflow } from '../../../../app/store/workflowSlice';
import { Questionnaire } from '../../../../interfaces/questionnaire.interface';
import { QuestionnaireTypes, questionnaireFeatureFlagCodename } from '../../../../services/questionnaires';
import { getQuestionnaireModalData, StartQuestionnaireModalData } from '../../../../services/surveyHelpers';
import { USER_TYPES } from '../../../../services/user';
import { useCanAddNewWorkflow } from '../../../../hooks/useCanAddNewWorkflow';
import { deleteContact } from '../../../../services/contact';
import LocationForm from '../LocationForm';
import  canAdminCompany from '../../../../services/adminUtils';
import { getHighestRankAssessmentBadgeLabel } from '../../../../utils/reportOutput';

type SiteSidebarProps = {
  isOpen: boolean;
  company: Company;
  site: Contact | null;
  canStartNewQuestionniare?: boolean;
  isAssessment?: boolean;
  onClose: () => void;
  companyCallback: (c: Company, updateType: 'edit' | 'delete') => void;
};

const SiteSidebar = (props: SiteSidebarProps) => {
  const { isOpen, company, site, isAssessment = false, onClose, canStartNewQuestionniare = false, companyCallback } = props;
  // react & redux
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.user);
  const questionnaires: Questionnaire[] = useAppSelector(state => state.questionnaires.results);
  const { site_baseline: siteBaseline } = useAppSelector((state) => state.surveys);
  const { workflows: fullWf } = useAppSelector((state) => state.workflow);
  const muiTheme = useTheme();
  // states
  const [assessmentModalOpen, setAssessmentModalOpen] = useState(false);
  const [isCreatingWorkflow, setIsCreatingWorkflow] = useState(false);
  const isCreatingWorkflowRef = useRef(false);
  const [workflowAnchorEl, setWorkflowAnchorEl] = useState<null | HTMLElement>(null);
  const [surveys, setSurveys] = useState<Survey[] | undefined>(undefined);
  const [assessmentSurveys, setAssessmentSurveys] = useState<undefined | AssessmentSurvey[]>(undefined);
  const [wfToDeactivate, setwfToDeactivate] = useState<undefined | Workflow>(undefined);
  const [inactiveWorkflows, setInactiveWorkflows] = useState<Workflow[]>([]);
  const hasQuestionnaireFlag = useFeatureFlagChecker();
  const workflowMap = useWorkflowMap();
  const [isStartQuestionnaireModalOpen, setIsStartQuestionnaireModalOpen] = useState(false);
  const [startQuestionnaireModalData, setStartQuestionnaireModalData] = useState<StartQuestionnaireModalData | null>(null);
  const [workflowToDeactivate, setWorkflowToDeactivate] = useState<undefined | Workflow>(undefined);
  const [pendingWorkflowType, setPendingWorkflowType] = useState<WorkflowTypeKey | undefined>(undefined);
  const buttonRef = React.useRef(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [editSiteOpen, setEditSiteOpen] = useState(false);
  const [deleteSiteOpen, setDeleteSiteOpen] = useState(false);
  const isMenuOpen = Boolean(anchorEl);
  // Questionnaires the user may start: published ones (everyone) plus unpublished
  // ones granted by their per-version feature flag. Newest first.
  const startableQuestionnaires = useMemo(() => sortQuestionnairesNewestFirst(
    questionnaires.filter(q => !!q.workflowType && (
      q.isPublished || hasQuestionnaireFlag(q.featureFlagCodename ?? questionnaireFeatureFlagCodename(q.version))
    )),
  ), [questionnaires, hasQuestionnaireFlag]);

  // local variables
  const workflows = useMemo(() => fullWf?.filter((wf) => wf.site === site?.id && wf.isActive), [fullWf, site]);
  const userCanAdminCompany = useMemo(() => canAdminCompany(user, company), [user, company]);
  const noWorkflowAssessmentSurveys: boolean = useMemo(() => {
    const wfs = fullWf?.filter((wf) => wf.site === site?.id);
    if (!wfs || wfs.length === 0) return true;
    return wfs.every((wf) => !wf?.assessmentSurvey && wf?.status !== 'submittedForAssessment');
  }, [fullWf, site]);

  const workflowOpen = Boolean(workflowAnchorEl);
  const wfList = useMemo(() => {
    const merged = workflows ? [...workflows, ...inactiveWorkflows] : [...inactiveWorkflows];
    return dedupeWorkflowsById(merged).sort((a, b) => b.id - a.id);
  }, [workflows, inactiveWorkflows]);

  const handleEditSiteOpen = useCallback((e: React.MouseEvent<HTMLLIElement>) => {
    e.stopPropagation();
    setAnchorEl(null);
    setEditSiteOpen(true);
  }, []);

  const handleDeleteSiteOpen = useCallback((e: React.MouseEvent<HTMLLIElement>) => {
    e.stopPropagation();
    setAnchorEl(null);
    setDeleteSiteOpen(true);
  }, []);

  const handleWorkflowClick = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    setWorkflowAnchorEl(event.currentTarget);
  }, []);

  const handleMenuClick = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  }, []);

  const handleMenuClose = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    setAnchorEl(null);
  }, []);

  // short/simple functions
  const handleWorkflowClose = useCallback(() => setWorkflowAnchorEl(null), []);
  const handleEditSiteClose = useCallback(() => setEditSiteOpen(false), []);
  const handleDeleteSiteClose = useCallback(() => setDeleteSiteOpen(false), []);

  useEffect(() => {
    dispatch(fetchAll({ unpublished: false }));
  }, []);

  // Auto-opens the workflow menu when canStartNewQuestionniare is true
  useEffect(() => {
    if (user && isOpen && canStartNewQuestionniare) {
      const timer = setTimeout(() => {
        if (buttonRef.current) {
          setWorkflowAnchorEl(buttonRef.current);
        }
      }, 100);
      return () => clearTimeout(timer);
    } else if (!isOpen) {
      setWorkflowAnchorEl(null);
    }
  }, [user, isOpen, canStartNewQuestionniare]);

  const handleCreateWorkflow = useCallback(
    async (type: WorkflowTypeKey) => {
      if (isCreatingWorkflowRef.current) return; // Prevent multiple simultaneous creations

      try {
        isCreatingWorkflowRef.current = true;
        setIsCreatingWorkflow(true);
        setWorkflowAnchorEl(null);
        let finalBaseline = siteBaseline; // Default published baseline
        if (!finalBaseline) {
          const res = await dispatch(fetchPublishedQuestionnaire(QuestionnaireTypes.SITE_BASELINE)).unwrap();
          finalBaseline = (res as QuestionnaireType).questionnaire.id;
        }

        // Use the baseline from the same version group as the selected questionnaire,
        // so a trial version pairs with its own baseline instead of the published one.
        const startedVersion = questionnaires.find(q => q.workflowType === type)?.version;
        const groupBaseline = startedVersion
          ? questionnaires.find(q => q.type === 'site_baseline' && q.version === startedVersion)
          : undefined;
        if (groupBaseline) finalBaseline = groupBaseline.id;

        const baselineSurvey = await createSurvey({
          questionnaire: finalBaseline as number,
          status: 'incomplete',
          company: company.id!,
          questionAnswers: [],
          site: site?.id!,
        });
        if (!baselineSurvey) return;

        await createNewWorkflow({
          workflowType: type,
          baseline: baselineSurvey.id,
          status: 'incomplete',
          serviceProvider: company.id!,
          site: site?.id,
        });
        navigate(`/questionnaire/${baselineSurvey.id!}/`);
      } catch (error) {
        console.log('creating workflow failed', error);
      } finally {
        isCreatingWorkflowRef.current = false;
        setIsCreatingWorkflow(false);
      }
    },
    [dispatch, navigate, company?.id, site?.id, questionnaires, siteBaseline, isCreatingWorkflowRef],
  );

  const handleStartQuestionnaire = useCallback( async (type: WorkflowTypeKey) => {
    if (!site?.id || isCreatingWorkflowRef.current) {
      return;
    }

    if (!workflows || workflows.length === 0) {
      handleCreateWorkflow(type);
      return;
    }

    const recentSiteWorkflow = workflows
      ?.filter((wf) => wf.site === site.id)
      .sort((a, b) => moment(b.createdAt).unix() - moment(a.createdAt).unix())[0];

    if (!recentSiteWorkflow) {
      return;
    }

    // Check if the recent workflow's questionnaire version is archived
    const recentSiteWorkflowQuestionnaireVersion = workflowMap[recentSiteWorkflow.workflowType]?.questionnaireVersion;
    const recentSiteWorkflowQuestionnaire = questionnaires.find((q) => q.version === recentSiteWorkflowQuestionnaireVersion);
    const isRSWfQArchived = recentSiteWorkflowQuestionnaire?.archiveDate && moment(recentSiteWorkflowQuestionnaire.archiveDate).isBefore(moment());

    const modalData = getQuestionnaireModalData(
      recentSiteWorkflow.status as WorkflowStatusInterface,
      workflowVersion(recentSiteWorkflow.workflowType),
      workflowVersion(type),
      assessmentSurveys?.some((s) => s.id === recentSiteWorkflow.assessmentSurvey && (!!s.bestPracticeRemediationCount || !!s.implementationGuidanceRemediationCount)),
    );

    if (!modalData.data || isRSWfQArchived) {
      // Skip modal, deactivate all other active workflows
      await handleDeativateWorkflows(workflows);
      handleCreateWorkflow(type);
      return;
    }
    setWorkflowToDeactivate(recentSiteWorkflow);
    setPendingWorkflowType(type);
    setStartQuestionnaireModalData(modalData);
    setIsStartQuestionnaireModalOpen(true);
  }, [workflows, site, assessmentSurveys, handleCreateWorkflow, isCreatingWorkflowRef]);

  const handleCloseStartQuestionnaireModal = useCallback(async (startWf: boolean) => {
    if (!startWf) {
      setIsStartQuestionnaireModalOpen(false);
      return;
    }

    // Deactivate all other active workflows
    try {
      if (!workflowToDeactivate || !workflows) return;

      handleDeativateWorkflows(workflows);
      if (pendingWorkflowType) handleCreateWorkflow(pendingWorkflowType);
    } catch (error) {
      console.error('Failed to deactivate workflow:', error);
    } finally {
      setIsStartQuestionnaireModalOpen(false);
      setWorkflowToDeactivate(undefined);
      setPendingWorkflowType(undefined);
    }
  }, [workflowToDeactivate, workflows, pendingWorkflowType]);

  // Site's shield cert + app names are embedded on the site object by the
  // company-detail endpoint (certs newest-first), so no company-wide lookup.
  const companyCertification = useMemo(() => site?.certifications?.[0], [site?.certifications]);

  const getApplicationName = useMemo(
    () => (ca: CompanyApplication) => ca.application?.name ?? 'Name Missing',
    [],
  );

  const canAddNewWorkflow = useCanAddNewWorkflow(workflows);

  const assessmentMap = useMemo(() => {
    if (!assessmentSurveys) return {};

    return assessmentSurveys.reduce<Record<number, AssessmentSurvey>>((mapObj, aSurvey) => {
      if (!aSurvey.id) return mapObj;
      mapObj[aSurvey.id] = aSurvey;
      return mapObj;
    }, {});
  }, [assessmentSurveys]);

  const surveyMap = useMemo(() => {
    if (!surveys) return {};
    return surveys.reduce<Record<number, Survey>>((mapObj, s) => {
      if (s.id != null) mapObj[s.id] = s;
      return mapObj;
    }, {});
  }, [surveys]);

  const mostRecentBlue = useMemo(() => recentBadgeByType(wfList, [BLUE_BADGE_LABEL]), [wfList]);
  const mostRecentGold = useMemo(() => recentBadgeByType(
    wfList,
    [
      GOLD_BADGE_LABEL,
      SILVER_BADGE_LABEL,
      GOLD_NEXT_GEN_BADGE_LABEL,
      GOLD_STAR_BADGE_LABEL,
    ],
  ), [wfList]);

  const handleFetchSurvey = useCallback(async (surveyId: number) => {
    const sur = await fetchSurvey(surveyId);
    setSurveys((cur) => {
      if (cur) return [...cur, sur];
      if (!cur) return [sur];
    });
  }, []);

  const handleFetchAssessmentSurvey = useCallback(async (assessmentSurveyId: number, status: string) => {
    const assSurvey = await fetchAssessmentSurvey(assessmentSurveyId);
    if (status !== 'Complete' || (status === 'Complete' && assSurvey.bestPracticeRemediationCount)) {
      setAssessmentSurveys((current) => {
        // No dups
        if (current?.some((s) => s.id === assSurvey.id)) {
          return current;
        }
        return [...(current || []), assSurvey];
      });
      return true;
    }
    return false;
  }, []);

  const deactivateWorkflow = async () => {
    if (!wfToDeactivate) return;
    await dispatch(updateWorkflow({ id: wfToDeactivate.id, isActive: false }));
    setwfToDeactivate(undefined);
  };

  const canDeactivate = (wf: Workflow) => user?.type === USER_TYPES.TPN_ADMIN && wf.isActive;

  const updateAssSur = (p: { assSur?: AssessmentSurvey; key: string; value: boolean }) =>
    p.assSur && updateAssessmentSurvey({ ...p.assSur, [p.key]: p.value });

  // for fetching survey or assessment survey when opening the sidebar and if it does not exists in workflows redux state
  useEffect(() => {
    const fetchData = async () => {
      if (!wfList.length) return;
      for (const workflow of wfList) {
        if (workflow?.assessmentSurvey && !assessmentSurveys?.find(sas => sas.id === workflow?.assessmentSurvey)) {
          const hasRemediationItems = await handleFetchAssessmentSurvey(workflow?.assessmentSurvey, workflow?.status);
          if (hasRemediationItems) continue;
        }
        if (!workflow?.bpSurvey) continue;
        if (workflow?.bpSurvey && !workflow?.assessment && !['submittedForAssessment', 'complete'].includes(workflow?.status.toLowerCase())) {
          if (!surveys?.find(ss => ss.id === workflow?.bpSurvey)) {
            handleFetchSurvey(workflow?.bpSurvey);
          }
        }
      }
    };
    fetchData();
  }, [wfList]);

  useEffect(() => {
    if (!site) return;
    setInactiveWorkflows([]);
    fetchWorkflows({
      site: site.id,
      service_provider: company.id,
    }, false).then(setInactiveWorkflows);
  }, [site]);

  const [siteCloudStatus, setSiteCloudStatus] = useState<BaselineStatusMap>({});

  const fetchSiteCloudStatus = async (baselineId: number) => {
    try {
      const siteCloudStatusData = await getSiteCloudStatus(baselineId);
      setSiteCloudStatus((prev) => ({
        ...prev,
        [baselineId]: siteCloudStatusData,
      }));
    } catch (error) {
      console.error('Failed to fetch site cloud status:', error);
      setSiteCloudStatus((prev) => ({
        ...prev,
        [baselineId]: null,
      }));
    }
  };

  const handleSiteDeleteSubmit = useCallback(async () => {
    if (!site) return;

    await deleteContact(site);
    const updatedCompany: Company = {
      ...company,
      locations: company.locations.filter((location: Contact) => location.id != site.id),
    };
    companyCallback(updatedCompany, 'delete');
    handleDeleteSiteClose();
  }, [company, site, companyCallback]);

  const handleEditSubmitSuccess = useCallback(
    (updatedCompany: Company) => {
      companyCallback(updatedCompany, 'edit');
      setEditSiteOpen(false);
    },
    [companyCallback],
  );

  useEffect(() => {
    if (!workflows || !isOpen) return;
    workflows.forEach(({ baseline }) => {
      const baselineId = typeof baseline === 'object' ? (baseline as any)?.id : baseline;
      if (!baselineId || siteCloudStatus[baselineId]) return;
      fetchSiteCloudStatus(baselineId);
    });
  }, [workflows, isOpen]);

  const renderMenuItem = useCallback(
    (questionnaire: Questionnaire) => {
      const type = questionnaire.workflowType as WorkflowTypeKey;
      const label = (
        <>
          {workflowLabels(type)}
          {!questionnaire.isPublished && (
            <Tooltip title="Unpublished — available via feature-flag trial access" arrow>
              <AccessTimeOutlined sx={{ fontSize: '0.9rem', ml: 0.5, verticalAlign: 'text-top', color: '#808080' }} />
            </Tooltip>
          )}
        </>
      );
      return canAddNewWorkflow(type) ? (
        <MenuItem key={questionnaire.id} onClick={() => handleStartQuestionnaire(type)}>
          {label}
        </MenuItem>
      ) : (
        <MenuItem key={questionnaire.id} disabled>
          {label}
        </MenuItem>
      );
    },
    [canAddNewWorkflow, handleStartQuestionnaire, workflowLabels],
  );

  const mainGoldBadgeLabel = useMemo(() =>
    mostRecentGold.badge &&
    fetchGoldBadgeLabel([mostRecentGold.badge], mostRecentGold.version, mostRecentGold.badge.title)
  , [mostRecentGold]);

  const mainBlueBadgeLabel = useMemo(() =>
    mostRecentBlue.badge && fetchBlueBadgeLabel([mostRecentBlue.badge], mostRecentBlue.version)
  , [mostRecentBlue]);

  const primaryShield = useMemo(() => {
    const isGoldExpired = (mainGoldBadgeLabel ?? '').toLowerCase().includes('expired');

    if (mostRecentGold.badge && isGoldExpired) {
      return mostRecentBlue.badge;
    }

    return mostRecentGold.badge || mostRecentBlue.badge;
  }, [mostRecentGold, mostRecentBlue, mainGoldBadgeLabel]);

  return (
    <Drawer
      anchor='right'
      open={isOpen}
      onClose={onClose}
      keepMounted={false}
      sx={(theme: Theme) => ({
        '& .MuiPaper-root': {
          width: 600,
          backgroundColor: theme.palette.background.default,
          [theme.breakpoints.down('sm')]: {
            width: 250,
          },
        },
      })}
    >
      <Box
        component='section'
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          p: '1.25rem',
          bgcolor: '#2F2D4F',
        }}
      >
        <Typography fontSize='1.063rem' color='#fff' fontWeight='500'>
          Site Details
        </Typography>
        <Box>
        {((userCanAdminCompany) ||
          user?.type === USER_TYPES.TPN_ADMIN) && (
          <Tooltip title="Manage" arrow>
            <Button
              variant='contained'
              onClick={handleMenuClick}
              startIcon={<MoreVert fontSize="small" />}
            >
              Manage Site
            </Button>
          </Tooltip>
        )}
        <IconButton sx={{ color: '#fff' }} onClick={onClose}>
          <Close sx={{ fontSize: '1.125rem' }} />
        </IconButton>
      </Box>
      <Menu
          anchorEl={anchorEl}
          open={isMenuOpen}
          onClose={handleMenuClose}
          sx={{ '& .MuiMenu-paper': { minWidth: 150,  color: '#737373' } }}
        >
          <MenuItem onClick={handleEditSiteOpen} sx={{ columnGap: 1, alignItems: 'center' }}>
            <EditOutlined fontSize="small"/>
            <Typography>Edit</Typography>
          </MenuItem>
        {(noWorkflowAssessmentSurveys || user?.type === USER_TYPES.TPN_ADMIN) && (
          <MenuItem onClick={handleDeleteSiteOpen} sx={{ columnGap: 1, alignItems: 'center' }}>
            <DeleteOutline fontSize="small"/>
            <Typography>Delete</Typography>
          </MenuItem>
        )}
        </Menu>
      </Box>
      <Box
        component='section'
        sx={{ display: 'flex', flexDirection: 'column', p: '1.25rem', borderBottom: '1px solid #DBE3EF' }}
      >
       <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', color: 'text.primary' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography>{site?.name}</Typography>
            </Box>
            { site?.address && (
              <Typography>
                {site.address}
                <br />{site.city}, {site.state} {site.country} {site.zipcode}
                {site?.address2 && (
                  <>
                    <br />
                    {site.address2}
                  </>
                )}
              </Typography>
            )}
            {site?.primaryContact && site?.primaryContact && (
              <Typography>
                {site.primaryContact.firstName} {site.primaryContact.lastName}
              </Typography>
            )}
            {site?.primaryContact && site?.primaryContact.contact && (
              <Typography>{site.primaryContact.contact.phoneNumber}</Typography>
            )}
            {site?.primaryContact && site?.primaryContact.email && <Typography>{site.primaryContact.email}</Typography>}
          </Box>
          {/* Main badge section in the sidebar */}
          <GetBadge
            isActive={company.isPublished}
            shield={primaryShield}
            goldBadgeLabel={mainGoldBadgeLabel}
            blueBadgeLabel={mainBlueBadgeLabel}
            certification={companyCertification}
            company={company.id}
          />
        </Box>
        <Typography sx={{ color: '#575757', fontWeight: 600, mt: '1rem' }}>Applications</Typography>
        <Box sx={{ display: 'flex', gap: '0.25rem', flexWrap: 'wrap', mt: '0.5rem' }}>
          {site?.companyApplications?.length === 0 && <Typography>-</Typography>}
          {site?.companyApplications?.map((ca) => {
            return (
              <Chip
                key={ca.id}
                label={getApplicationName(ca)}
                size='small'
                sx={(theme) => ({
                  bgcolor: theme.palette.chips?.app,
                  color: '#FFFFFFCC',
                  '& .MuiChip-label': { px: '1rem' },
                })}
              ></Chip>
            );
          })}
        </Box>
      </Box>
      <Box
        component='section'
        sx={{ display: 'flex', flexDirection: 'column', p: '1.25rem', borderBottom: '1px solid #DBE3EF' }}
      >
        <Typography sx={{ color: '#575757', fontWeight: 600 }}>Services</Typography>
        <Box sx={{ display: 'flex', gap: '0.25rem', flexWrap: 'wrap', mt: '0.5rem' }}>
          {site?.services?.map((service) => {
            return (
              <Chip
                key={service.id}
                label={service.name}
                size='small'
                sx={(theme) => ({
                  bgcolor: theme.palette.chips?.service,
                  color: theme.palette.chips?.contrastText,
                  '& .MuiChip-label': { px: '1rem' },
                })}
              ></Chip>
            );
          })}
          {site?.services?.length === 0 && <Typography>-</Typography>}
        </Box>
      </Box>
      {!isAssessment && <Box
        component='section'
        sx={{ display: 'flex', flexDirection: 'column', p: '1.25rem', borderBottom: '1px solid #DBE3EF' }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <Typography sx={{ color: '#575757', fontWeight: 600 }}>Questionnaires</Typography>
          {user && (user.type === USER_TYPES.VENDOR_ADMIN || user.type === USER_TYPES.TPN_ADMIN) && (
            <>
              <LoadingButton
                ref={buttonRef}
                disabled={user?.type === USER_TYPES.TPN_ADMIN}
                variant='contained'
                startIcon={<Add />}
                size='small'
                loadingPosition='start'
                aria-controls={workflowOpen ? 'basic-menu' : undefined}
                aria-haspopup='true'
                aria-expanded={workflowOpen ? 'true' : undefined}
                loading={isCreatingWorkflow}
                onClick={handleWorkflowClick}
              >
                New Questionnaire
              </LoadingButton>
              <Menu
                id='basic-menu'
                anchorEl={workflowAnchorEl}
                open={workflowOpen}
                onClose={handleWorkflowClose}
                MenuListProps={{
                  'aria-labelledby': 'basic-button',
                }}
              >
                {startableQuestionnaires.length
                  ? startableQuestionnaires.map((questionnaire) => renderMenuItem(questionnaire))
                  : <MenuItem disabled>No new Questionnaires</MenuItem>}
              </Menu>
            </>
          )}
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', rowGap: '1rem', mt: '1rem' }}>
          {wfList?.length === 0 && <Typography>-</Typography>}
          {wfList?.map((workflow, i) => {
            return (
              <Grid container key={workflow.id} sx={{ py: 1 }}>
                {i !== 0 && <Divider />}
                <Grid item xs={2}>
                  <GetBadge
                    isActive={company.isPublished}
                    shield={selectBadgeFromArray(workflow.badges as Badge[], true)}
                    assessmentSurvey={
                      workflow.assessmentSurvey != null
                        ? assessmentMap[workflow.assessmentSurvey]
                        : undefined
                    }
                    assessor={workflow?.assignedAssessor as User | undefined}
                    certification={companyCertification}
                    company={company.id}
                    goldBadgeLabel={fetchGoldBadgeLabel(workflow.badges as Badge[], workflowLabels(workflow.workflowType), getHighestRankAssessmentBadgeLabel(workflow.badges as Badge[]))}
                    blueBadgeLabel={fetchBlueBadgeLabel(workflow.badges as Badge[], workflowLabels(workflow.workflowType))}
                  />
                </Grid>
                <Grid item xs={canDeactivate(workflow) ? 5 : 6}>
                  <Box sx={{ color: '#1876D2', fontWeight: 700 }}>
                    <WorkflowStatus
                      hasGoldExpired={[fetchGoldBadgeLabel(workflow.badges as Badge[], workflowLabels(workflow.workflowType)) || ''].toString().includes('Expired')}
                      hasBlueExpired={[fetchBlueBadgeLabel(workflow.badges as Badge[], workflowLabels(workflow.workflowType)) || ''].toString().includes('Expired')}
                      workflow={workflow}
                      company={company}
                      survey={
                        workflow.bpSurvey != null
                          ? surveyMap[workflow.bpSurvey]
                          : undefined
                      }
                      assessmentSurvey={
                      workflow.assessmentSurvey != null
                        ? assessmentMap[workflow.assessmentSurvey]
                        : undefined
                    }
                    />
                  </Box>
                </Grid>
                <Grid item xs={4}>
                  <WorkflowButtons
                    company={company}
                    workflow={workflow}
                    survey={
                        workflow.bpSurvey != null
                          ? surveyMap[workflow.bpSurvey]
                          : undefined
                      }
                    assessmentSurvey={
                      workflow.assessmentSurvey != null
                        ? assessmentMap[workflow.assessmentSurvey]
                        : undefined
                    }
                  />
                </Grid>
                {canDeactivate(workflow) && (
                  <Grid item xs={1}>
                    <IconButton onClick={() => setwfToDeactivate(workflow)}>
                      <Delete />
                    </IconButton>
                  </Grid>
                )}
                {workflow.baseline && siteCloudStatus[workflow.baseline] && (
                  <>
                    <Grid item xs={2}></Grid>
                    <Grid item xs={5}>
                      <Stack spacing={0.5} justifyContent='flex-start' alignItems='flex-start' ml='-16px' mt='8px'>
                        <Typography sx={{ fontWeight: 'bold', ml: '16px', color: muiTheme.palette.customThemeColors?.darkGrey }}>Scope</Typography>
                        <FormGroup>
                          <FormControlLabel
                            labelPlacement='start'
                            disabled
                            control={
                              <Switch
                                checked={!!siteCloudStatus[workflow.baseline]?.site || !!siteCloudStatus[workflow.baseline]?.both}
                              />
                            }
                            label={workflow?.site ? 'Site' : 'On-Prem'}
                          />
                          <FormControlLabel
                            labelPlacement='start'
                            disabled
                            control={
                              <Switch
                                checked={!!siteCloudStatus[workflow.baseline]?.cloud || !!siteCloudStatus[workflow.baseline]?.both}
                              />
                            }
                            label='Cloud'
                          />
                        </FormGroup>
                      </Stack>
                    </Grid>
                    <Grid item xs={5}>
                      <Stack spacing={0.5} justifyContent='flex-start' alignItems='flex-start' ml='-16px' mt='8px'>
                      { workflow.assessmentSurvey != null && (
                          <>
                            <Typography sx={{ fontWeight: 'bold', ml: '16px', color: muiTheme.palette.customThemeColors?.darkGrey }}>Type</Typography>
                            { assessmentMap[workflow.assessmentSurvey] ? (
                            <FormGroup>
                              <FormControlLabel
                                labelPlacement='start'
                                disabled={user?.type !== USER_TYPES.TPN_ADMIN}
                                control={
                                <Switch
                                  defaultChecked={ assessmentMap[workflow.assessmentSurvey]?.isInPerson }
                                  onChange={(e, v) =>
                                    updateAssSur({
                                      assSur:
                                        workflow.assessmentSurvey != null
                                          ? assessmentMap[workflow.assessmentSurvey]
                                          : undefined,
                                      key: 'isInPerson',
                                      value: v,
                                    })
                                  }
                                />
                              }
                              label='On Site'
                            />
                            </FormGroup>
                            ) : (
                              <Stack spacing={1} sx={{ px: 2, py: 1 }}>
                                <Skeleton variant="text" width={80} height={24} />
                              </Stack>
                            )}
                          </>
                      )}
                      </Stack>
                    </Grid>
                  </>
                )}
              </Grid>
            );
          })}
        </Box>
      </Box>}
      <Box component='section' sx={{ display: 'flex', flexDirection: 'column', p: '1.25rem' }}>
        <Typography sx={{ color: '#575757', fontWeight: 600, mb: '1rem' }}>Certificates</Typography>
        {(!site?.certifications || site.certifications.length === 0) && <Typography>-</Typography>}
        {site?.certifications?.map((companyCert) => (
          <Box
            key={companyCert.id}
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              mb: '0.5rem',
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Typography sx={{ color: '#848484' }}>{companyCert.certification.title}</Typography>
              <Box sx={{ display: 'flex', columnGap: '0.25rem', alignItems: 'center' }}>
                <Typography sx={{ color: '#DF8063', fontSize: '0.9rem' }}>
                  {moment(companyCert.expirationDate).format('MM/DD/YYYY')}
                </Typography>
                <Tooltip title='Expiry Date'>
                  <AccessTimeOutlined sx={{ color: '#808080', fontSize: '0.9rem' }} />
                </Tooltip>
              </Box>
            </Box>
            <Tooltip title={companyCert.adminApproved ? 'Accepted' : 'Rejected'}>
              {companyCert.adminApproved ? (
                <CheckCircle sx={{ color: '#7DBF29' }} />
              ) : (
                <Cancel sx={{ color: '#DF8063' }} />
              )}
            </Tooltip>
          </Box>
        ))}
      </Box>
      <AssessmentForm
        modalOpen={assessmentModalOpen}
        company={company}
        closeCallback={(updatedWorkflow?: Workflow) => {
          setAssessmentModalOpen(false);
          if (updatedWorkflow) {
            // Refetch workflows to update the list
            fetchWorkflows({
              site: site?.id,
              service_provider: company.id,
            }, false).then(setInactiveWorkflows);
          }
        }}
      />
      { startQuestionnaireModalData &&
        <StartNewQuestionnaireModal
        isOpen={isStartQuestionnaireModalOpen}
        handleClose={handleCloseStartQuestionnaireModal}
        startQuestionnaireModalData={startQuestionnaireModalData}
      />
      }
      <StandardDialog
        title='Deactivate Workflow'
        isOpen={!!wfToDeactivate}
        handleClose={() => setwfToDeactivate(undefined)}
      >
        <Typography>Are you sure you wish to deactivate this workflow?</Typography>
        <StandardDialogActions>
          <Button variant='outlined' onClick={() => setwfToDeactivate(undefined)}>
            Cancel
          </Button>
          <Button variant='contained' color='error' onClick={() => deactivateWorkflow()}>
            Deactivate
          </Button>
        </StandardDialogActions>
      </StandardDialog>
      <StandardDialog title={`Edit ${site?.name}`} handleClose={handleEditSiteClose} isOpen={editSiteOpen}>
        { site && (
          <LocationForm
            successCallback={handleEditSubmitSuccess}
            submitButtonText='Update Site'
            company={company}
            location={site}
            canModifyAddress={noWorkflowAssessmentSurveys || user?.type === USER_TYPES.TPN_ADMIN}
          />
        )}
      </StandardDialog>
      <StandardDialog title={`Delete ${site?.name}?`} handleClose={handleDeleteSiteClose} isOpen={deleteSiteOpen}>
        <Typography>Are you sure you want to delete {site?.name}?</Typography>
        <StandardDialogActions>
          <Button variant='outlined' onClick={() => handleDeleteSiteClose()}>
            Cancel
          </Button>
          <Button
            variant='outlined'
            color='error'
            startIcon={<Delete />}
            onClick={() => handleSiteDeleteSubmit()}
            disabled={false}
          >
            Delete
          </Button>
        </StandardDialogActions>
      </StandardDialog>
    </Drawer>
  );
};

export default SiteSidebar;
